import { useCallback } from 'react';

import { Checkbox, FormLabel, StyledFlex, StyledMargin, TextEditor } from '@dreryk/react-components';
import { FieldController, TranslatableText } from '@dreryk/react-common';
import { TextEditorValue } from '@dreryk/react-text-editor';

import { Field } from '../types';
import { translationKeys } from '../translations';
import { useTextEditorFieldAiEdit } from '../textEditorField/useTextEditorFieldAiEdit';
import { useTextEditorFieldDictation } from '../textEditorField/useTextEditorFieldDictation';

import { StyledSummaryModalField } from './AiAssistantMainMenuTileItemSummaryModal.styled';

const t = translationKeys.mainMenuTileItem.summaryModal;

const SUMMARY_TEXT_EDITOR_MAX_CONTENT_LENGTH = 10000;
const SUMMARY_TEXT_EDITOR_MAX_HTML_CONTENT_LENGTH = 50000;

type AiAssistantMainMenuTileItemSummaryModalFieldProps = {
   controller: FieldController<TextEditorValue | null>;
   transferController: FieldController<boolean>;
   redactionField: Field;
   label: TranslatableText;
   testId: string;
};

// eslint-disable-next-line max-lines-per-function
export const AiAssistantMainMenuTileItemSummaryModalField = (
   props: AiAssistantMainMenuTileItemSummaryModalFieldProps
): JSX.Element => {
   const getHtml = useCallback(
      () => props.controller.control!.getValues(props.controller.name)?.getFormattedValue() ?? '',
      [props.controller.control, props.controller.name]
   );

   const setHtml = useCallback(
      (html: string) => {
         props.controller.control!.setValue(props.controller.name, html ? TextEditorValue.fromFormatted(html) : null, {
            shouldDirty: true,
            shouldValidate: true,
         });
      },
      [props.controller.control, props.controller.name]
   );

   const dictation = useTextEditorFieldDictation({ getHtml: getHtml, setHtml: setHtml });
   const aiEdit = useTextEditorFieldAiEdit({ field: props.redactionField, getHtml: getHtml, setHtml: setHtml });

   const handleModalViewTextChange = useCallback(
      (value: TextEditorValue | null) => {
         props.controller.control!.setValue(props.controller.name, value, {
            shouldDirty: true,
            shouldValidate: true,
         });
      },
      [props.controller.control, props.controller.name]
   );

   const getModalViewInitialValue: () => TextEditorValue | null = useCallback(
      () => props.controller.control!.getValues(props.controller.name),
      [props.controller]
   );

   return (
      <StyledFlex direction={'column'} gap={8}>
         <StyledFlex justifyContent={'space-between'} alignItems={'center'}>
            <FormLabel label={props.label} testId={props.testId} />
            <StyledMargin top={2}>
               <Checkbox
                  controller={props.transferController}
                  label={t.transferToVisit}
                  testId={`${props.testId}Transfer`}
               />
            </StyledMargin>
         </StyledFlex>
         <StyledSummaryModalField>
            <TextEditor
               controller={props.controller}
               testId={props.testId}
               maxLength={SUMMARY_TEXT_EDITOR_MAX_CONTENT_LENGTH}
               maxHtmlLength={SUMMARY_TEXT_EDITOR_MAX_HTML_CONTENT_LENGTH}
            >
               <>
                  <TextEditor.Dictation
                     testId={'dictation'}
                     onBeforeStart={dictation.handleBeforeStart}
                     onStart={dictation.handleStart}
                     onStop={dictation.handleStop}
                  />

                  <TextEditor.Decision
                     testId={'editDecision'}
                     onReject={aiEdit.handleReject}
                     onAccept={aiEdit.handleAccept}
                  >
                     {({ show }) => <TextEditor.Edit testId={'edit'} onEdit={aiEdit.handleEdit} onProcessed={show} />}
                  </TextEditor.Decision>

                  {aiEdit.isPendingDecision ? null : (
                     <TextEditor.ModalView
                        testId={`${props.testId}ModalView`}
                        label={props.label}
                        onChange={handleModalViewTextChange}
                        initialValue={getModalViewInitialValue}
                        maxLength={SUMMARY_TEXT_EDITOR_MAX_CONTENT_LENGTH}
                     />
                  )}
               </>
            </TextEditor>
         </StyledSummaryModalField>
      </StyledFlex>
   );
};
